/*
  IW Nuevo: brands page filter (phase 2.6, T41). Markup: sections/iw-brands.liquid.

  /pages/marcas has two instances of the section: the official group, which carries the category chips and the A–Z
  index, and the grey group. The controls act on every instance on the page:
  - Chips ([data-iw-cat], "all" or a category handle): show the cards whose data-cats holds that handle, set
    aria-pressed on the chips, update each group's count (aria-live) and show a group's empty state when none of its
    cards is left (a group without an empty state, the grey one, is hidden instead).
  - A–Z index ([data-iw-letter]): each letter links to the first visible card that starts with it, official group
    first (document order). A letter without a visible brand loses its href (so it is not a link nor focusable),
    gets aria-hidden and the is-off class. Recomputed on every filter.
  No JavaScript: the section renders the chips, the index and the second intro sentence with the hidden attribute
  ([data-iw-marcas-js]), so every brand stays listed and nothing pretends to filter. setup() reveals them.
  Counts: the server renders each count as "<n> marcas" on one line. The guard compares after collapsing whitespace,
  so the first setup() leaves an unchanged count alone and the aria-live region is not announced on load.
  Card ids: the section already makes them unique inside one instance (marca-<handle>, -2, -3...). setup() does the
  same across instances: the first card in document order keeps its id, a later duplicate gets the next free suffix.
  Theme editor: a re-rendered section brings new markup and may run this file again. The click listener is installed
  once (window.iwBrands guards it), and every pass re-reads the DOM, so setup() is safe to call any number of times.
*/
(() => {
  'use strict';

  if (window.iwBrands) {
    window.iwBrands.setup();
    return;
  }

  let current = 'all';

  // First character of a brand name, as an index key: '0-9' for digits, A–Z for letters (accents removed),
  // '' for anything else (that brand is never an index target).
  const letterOf = (name) => {
    const first = name.trim().normalize('NFD').replace(/[̀-ͯ]/g, '').charAt(0).toUpperCase();
    if (/[0-9]/.test(first)) return '0-9';
    return /[A-Z]/.test(first) ? first : '';
  };

  const apply = () => {
    // A filter whose chip is gone (the editor turned the chips off) would hide brands with no way back.
    const chips = document.querySelectorAll('[data-iw-cat]');
    if (!Array.from(chips).some((chip) => chip.dataset.iwCat === current)) current = 'all';

    chips.forEach((chip) => {
      chip.setAttribute('aria-pressed', String(chip.dataset.iwCat === current));
    });

    const firstByLetter = {};
    document.querySelectorAll('[data-iw-marcas-group]').forEach((group) => {
      let visible = 0;
      group.querySelectorAll('[data-iw-brand]').forEach((item) => {
        const cats = (item.dataset.cats || '').split(' ');
        const show = current === 'all' || cats.includes(current);
        item.hidden = !show;
        if (!show) return;
        visible += 1;
        const letter = letterOf(item.dataset.name || '');
        const link = item.querySelector('a[id]');
        if (letter && link && !firstByLetter[letter]) firstByLetter[letter] = link.id;
      });

      const count = group.querySelector('[data-iw-marcas-count]');
      if (count) {
        const text = `${visible} ${visible === 1 ? count.dataset.one : count.dataset.many}`;
        // Only when it changes, so the live region does not repeat itself.
        if (count.textContent.replace(/\s+/g, ' ').trim() !== text) count.textContent = text;
      }

      // A group without an empty text (the grey one) disappears instead of saying it is empty.
      const empty = group.querySelector('[data-iw-marcas-empty]');
      if (empty) empty.hidden = visible > 0;
      else group.hidden = visible === 0;
    });

    document.querySelectorAll('[data-iw-letter]').forEach((letter) => {
      const target = firstByLetter[letter.dataset.iwLetter];
      if (target) {
        letter.setAttribute('href', `#${target}`);
        letter.setAttribute('aria-label', letter.dataset.iwLabel || letter.textContent.trim());
        letter.removeAttribute('aria-hidden');
        letter.classList.remove('is-off');
      } else {
        letter.removeAttribute('href');
        letter.removeAttribute('aria-label');
        letter.setAttribute('aria-hidden', 'true');
        letter.classList.add('is-off');
      }
    });
  };

  // Card ids must be unique on the page, because the A–Z index links to them. The section dedupes inside one
  // instance; this covers the same name in two instances. Document order wins, so existing anchors do not change.
  const dedupeIds = () => {
    const links = Array.from(document.querySelectorAll('[data-iw-brand] > a[id]'));
    const taken = new Set(links.map((link) => link.id));
    const seen = new Set();
    links.forEach((link) => {
      if (seen.has(link.id)) {
        let n = 2;
        while (taken.has(`${link.id}-${n}`)) n += 1;
        link.id = `${link.id}-${n}`;
        taken.add(link.id);
      }
      seen.add(link.id);
    });
  };

  const setup = () => {
    document.querySelectorAll('[data-iw-marcas-js]').forEach((element) => {
      element.hidden = false;
    });
    dedupeIds();
    apply();
  };

  document.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-iw-cat]');
    if (!chip) return;
    current = chip.dataset.iwCat;
    apply();
  });

  document.addEventListener('shopify:section:load', setup);
  // The unloaded section is still in the DOM while the event runs: recount once it is gone.
  document.addEventListener('shopify:section:unload', () => window.setTimeout(apply, 0));

  window.iwBrands = { setup };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
